import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export interface MentorshipRequest {
  id: string;
  entrepreneur_id: string;
  mentor_id: string;
  topic_id: string;
  message: string;
  status: "pending" | "accepted" | "declined" | "closed" | "cancelled";
  thread_id: string | null;
  initiated_by: "entrepreneur" | "mentor";
  created_at: string;
  updated_at: string;
  cancelled_at: string | null;
  cancelled_by: string | null;
  cancellation_reason: string | null;
  cancellation_actor: string | null;
  entrepreneur?: {
    id: string;
    full_name: string;
    avatar_url: string | null;
  };
  mentor?: {
    id: string;
    full_name: string;
    avatar_url: string | null;
  };
  topic?: {
    id: string;
    name: string;
  };
}

// Fetch mentorship requests sent by the current entrepreneur
export function useSentMentorshipRequests() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["mentorship-requests-sent", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from("mentorship_requests")
        .select(`
          *,
          mentor:profiles!mentorship_requests_mentor_id_fkey(id, full_name, avatar_url),
          topic:mentor_topics(id, name)
        `)
        .eq("entrepreneur_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      
      // Transform the data to match the expected interface
      return (data || []).map((item) => ({
        ...item,
        status: item.status as MentorshipRequest["status"],
        mentor: Array.isArray(item.mentor) ? item.mentor[0] : item.mentor,
        topic: Array.isArray(item.topic) ? item.topic[0] : item.topic,
      })) as MentorshipRequest[];
    },
    enabled: !!user?.id,
    staleTime: 2 * 60 * 1000, // Cache for 2 minutes
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// Fetch mentorship requests received by the current mentor
// Note: Returns ALL requests where mentor_id matches, regardless of topic selections
// This ensures mentors see all requests sent to them directly
export function useReceivedMentorshipRequests() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["mentorship-requests-received", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from("mentorship_requests")
        .select(`
          *,
          entrepreneur:profiles!mentorship_requests_entrepreneur_id_fkey(id, full_name, avatar_url),
          topic:mentor_topics(id, name)
        `)
        .eq("mentor_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      
      // Transform the data to match the expected interface
      return (data || []).map((item) => ({
        ...item,
        status: item.status as MentorshipRequest["status"],
        entrepreneur: Array.isArray(item.entrepreneur) ? item.entrepreneur[0] : item.entrepreneur,
        topic: Array.isArray(item.topic) ? item.topic[0] : item.topic,
      })) as MentorshipRequest[];
    },
    enabled: !!user?.id,
    staleTime: 2 * 60 * 1000, // Cache for 2 minutes
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// Fetch mentor-initiated mentorship offers received by the current entrepreneur
export function useReceivedMentorOffers() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["mentorship-offers-received", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from("mentorship_requests")
        .select(`
          *,
          mentor:profiles!mentorship_requests_mentor_id_fkey(id, full_name, avatar_url),
          topic:mentor_topics(id, name)
        `)
        .eq("entrepreneur_id", user.id)
        .eq("initiated_by", "mentor")
        .order("created_at", { ascending: false });

      if (error) throw error;
      
      return (data || []).map((item) => ({
        ...item,
        status: item.status as MentorshipRequest["status"],
        mentor: Array.isArray(item.mentor) ? item.mentor[0] : item.mentor,
        topic: Array.isArray(item.topic) ? item.topic[0] : item.topic,
      })) as MentorshipRequest[];
    },
    enabled: !!user?.id,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// Count pending mentor offers for entrepreneurs
export function usePendingMentorOfferCount() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["mentorship-offers-pending-count", user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;

      const { count, error } = await supabase
        .from("mentorship_requests")
        .select("*", { count: "exact", head: true })
        .eq("entrepreneur_id", user.id)
        .eq("initiated_by", "mentor")
        .eq("status", "pending");

      if (error) throw error;
      return count || 0;
    },
    enabled: !!user?.id,
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}


// Note: Counts ALL pending requests where mentor_id matches, regardless of topic selections
export function usePendingMentorshipRequestCount() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["mentorship-requests-pending-count", user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;

      const { count, error } = await supabase
        .from("mentorship_requests")
        .select("*", { count: "exact", head: true })
        .eq("mentor_id", user.id)
        .eq("status", "pending");

      if (error) throw error;
      return count || 0;
    },
    enabled: !!user?.id,
    staleTime: 1 * 60 * 1000, // Cache for 1 minute - this is badge count, needs to be relatively fresh
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// Create a new mentorship request (entrepreneur → mentor)
export function useCreateMentorshipRequest() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      mentorId,
      topicId,
      message,
    }: {
      mentorId: string;
      topicId: string;
      message: string;
    }) => {
      if (!user?.id) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("mentorship_requests")
        .insert({
          entrepreneur_id: user.id,
          mentor_id: mentorId,
          topic_id: topicId,
          message,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mentorship-requests-sent"] });
      queryClient.invalidateQueries({ queryKey: ["mentorship-requests-pending-count"] });
      queryClient.invalidateQueries({ queryKey: ["new-connections-indicator"] });
      queryClient.invalidateQueries({ queryKey: ["existing-mentorship-request"] });
      toast.success("Mentorship request sent! The mentor will review it in their own time.");
    },
    onError: (error) => {
      toast.error("Failed to send request: " + error.message);
    },
  });
}

// Create a mentor-initiated mentorship request (mentor → entrepreneur)
// When isAdmin is true, auto-accepts the request immediately via RPC
export function useCreateMentorInitiatedRequest() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      entrepreneurId,
      topicId,
      message = "",
      isAdmin = false,
    }: {
      entrepreneurId: string;
      topicId?: string | null;
      message?: string;
      isAdmin?: boolean;
    }) => {
      if (!user?.id) throw new Error("Not authenticated");

      const insertData: Record<string, unknown> = {
        entrepreneur_id: entrepreneurId,
        mentor_id: user.id,
        message,
        initiated_by: "mentor",
      };
      if (topicId) {
        insertData.topic_id = topicId;
      }

      const { data, error } = await supabase
        .from("mentorship_requests")
        .insert(insertData as any)
        .select()
        .single();

      if (error) throw error;

      let autoAccepted = false;

      // Admin auto-accept: immediately accept via RPC to create thread
      if (isAdmin && data?.id) {
        const { error: acceptError } = await supabase.rpc(
          "accept_mentorship_request",
          { p_request_id: data.id }
        );
        if (acceptError) {
          console.error("Auto-accept failed:", acceptError.message);
          // Request was created but auto-accept failed — still return success
        } else {
          autoAccepted = true;
        }
      }

      return { ...data, autoAccepted };
    },
    onSuccess: (_data) => {
      queryClient.invalidateQueries({ queryKey: ["mentorship-requests-sent"] });
      queryClient.invalidateQueries({ queryKey: ["mentorship-requests-received"] });
      queryClient.invalidateQueries({ queryKey: ["mentorship-requests-pending-count"] });
      queryClient.invalidateQueries({ queryKey: ["mentorship-offers-received"] });
      queryClient.invalidateQueries({ queryKey: ["mentorship-offers-pending-count"] });
      queryClient.invalidateQueries({ queryKey: ["new-connections-indicator"] });
      queryClient.invalidateQueries({ queryKey: ["existing-mentorship-request"] });
      queryClient.invalidateQueries({ queryKey: ["message-threads"] });
      const msg = _data.autoAccepted
        ? "Connected!"
        : "Mentorship request sent!";
      toast.success(msg);
    },
    onError: (error) => {
      toast.error("Failed to send offer: " + error.message);
    },
  });
}



// Accept a mentorship request (mentor only)
export function useAcceptMentorshipRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (requestId: string) => {
      const { data, error } = await supabase.rpc("accept_mentorship_request", {
        p_request_id: requestId,
      });

      if (error) throw error;
      return data as string; // Returns the thread_id
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mentorship-requests-received"] });
      queryClient.invalidateQueries({ queryKey: ["mentorship-requests-pending-count"] });
      queryClient.invalidateQueries({ queryKey: ["mentorship-offers-received"] });
      queryClient.invalidateQueries({ queryKey: ["mentorship-offers-pending-count"] });
      queryClient.invalidateQueries({ queryKey: ["message-threads"] });
      queryClient.invalidateQueries({ queryKey: ["new-connections-indicator"] });
      toast.success("Mentorship request accepted! A conversation has been started.");
    },
    onError: (error) => {
      toast.error("Failed to accept request: " + error.message);
    },
  });
}

// Decline a mentorship request (mentor only)
export function useDeclineMentorshipRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (requestId: string) => {
      const { error } = await supabase
        .from("mentorship_requests")
        .update({ status: "declined" })
        .eq("id", requestId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mentorship-requests-received"] });
      queryClient.invalidateQueries({ queryKey: ["mentorship-requests-pending-count"] });
      queryClient.invalidateQueries({ queryKey: ["mentorship-offers-received"] });
      queryClient.invalidateQueries({ queryKey: ["mentorship-offers-pending-count"] });
      queryClient.invalidateQueries({ queryKey: ["new-connections-indicator"] });
      toast.success("Request declined.");
    },
    onError: (error) => {
      toast.error("Failed to decline request: " + error.message);
    },
  });
}

// Cancel a mentorship request (entrepreneur or admin)
export function useCancelMentorshipRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ requestId, reason }: { requestId: string; reason?: string }) => {
      const { data, error } = await supabase.rpc("cancel_mentorship_request", {
        p_request_id: requestId,
        p_reason: reason || null,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mentorship-requests-sent"] });
      queryClient.invalidateQueries({ queryKey: ["mentorship-requests-received"] });
      queryClient.invalidateQueries({ queryKey: ["mentorship-requests-pending-count"] });
      queryClient.invalidateQueries({ queryKey: ["admin-mentorship-requests"] });
      queryClient.invalidateQueries({ queryKey: ["new-connections-indicator"] });
      queryClient.invalidateQueries({ queryKey: ["existing-mentorship-request"] });
      toast.success("Mentorship request cancelled.");
    },
    onError: (error) => {
      toast.error("Failed to cancel request: " + error.message);
    },
  });
}

// Fetch all mentorship requests for admin
export function useAdminMentorshipRequests(statusFilter?: string) {
  return useQuery({
    queryKey: ["admin-mentorship-requests", statusFilter],
    queryFn: async () => {
      let query = supabase
        .from("mentorship_requests")
        .select(`
          *,
          entrepreneur:profiles!mentorship_requests_entrepreneur_id_fkey(id, full_name, avatar_url),
          mentor:profiles!mentorship_requests_mentor_id_fkey(id, full_name, avatar_url),
          topic:mentor_topics(id, name)
        `)
        .order("created_at", { ascending: false });

      if (statusFilter && statusFilter !== "all") {
        query = query.eq("status", statusFilter);
      }

      const { data, error } = await query;
      if (error) throw error;

      return (data || []).map((item) => ({
        ...item,
        status: item.status as MentorshipRequest["status"],
        entrepreneur: Array.isArray(item.entrepreneur) ? item.entrepreneur[0] : item.entrepreneur,
        mentor: Array.isArray(item.mentor) ? item.mentor[0] : item.mentor,
        topic: Array.isArray(item.topic) ? item.topic[0] : item.topic,
      })) as MentorshipRequest[];
    },
  });
}

// Check if entrepreneur can message a mentor (has connection or accepted mentorship)
export function useCanMessageMentor(mentorId: string | null) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["can-message-mentor", user?.id, mentorId],
    queryFn: async () => {
      if (!user?.id || !mentorId) return { canMessage: false, reason: "not_authenticated" };

      // Check for accepted connection in unified connections table
      const { data: connection } = await supabase
        .from("connections")
        .select("id")
        .or(`and(requester_id.eq.${user.id},recipient_id.eq.${mentorId}),and(requester_id.eq.${mentorId},recipient_id.eq.${user.id})`)
        .eq("status", "accepted")
        .maybeSingle();

      if (connection) {
        return { canMessage: true, reason: "connection" };
      }

      // Check for accepted mentorship request
      const { data: mentorship } = await supabase
        .from("mentorship_requests")
        .select("id")
        .or(`and(entrepreneur_id.eq.${user.id},mentor_id.eq.${mentorId}),and(mentor_id.eq.${user.id},entrepreneur_id.eq.${mentorId})`)
        .eq("status", "accepted")
        .maybeSingle();

      if (mentorship) {
        return { canMessage: true, reason: "mentorship" };
      }

      // Check for pending mentorship request
      const { data: pendingMentorship } = await supabase
        .from("mentorship_requests")
        .select("id")
        .eq("entrepreneur_id", user.id)
        .eq("mentor_id", mentorId)
        .eq("status", "pending")
        .maybeSingle();

      if (pendingMentorship) {
        return { canMessage: false, reason: "pending_mentorship" };
      }

      return { canMessage: false, reason: "no_relationship" };
    },
    enabled: !!user?.id && !!mentorId,
  });
}

// Get existing mentorship request between two users (checks both directions)
export function useExistingMentorshipRequest(otherUserId: string | null) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["existing-mentorship-request", user?.id, otherUserId],
    queryFn: async () => {
      if (!user?.id || !otherUserId) return null;

      const { data, error } = await supabase
        .from("mentorship_requests")
        .select("id, status, entrepreneur_id, mentor_id")
        .or(
          `and(entrepreneur_id.eq.${user.id},mentor_id.eq.${otherUserId}),and(entrepreneur_id.eq.${otherUserId},mentor_id.eq.${user.id})`
        )
        .in("status", ["pending", "accepted"])
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id && !!otherUserId,
  });
}
